import { useRef, useEffect } from 'react';

import { AdCampaignQueryFilter, AdCampaignQueryPagination, AdCampaignQuerySort } from '@shared/api/graphql/generated';
import { DEFAULT_SHORT_PAGINATION, DEFAULT_PAGINATION_INFO } from 'application/pages/constants';
import { useAccountCompanyIds } from 'application/hooks';

import {
  AD_CAMPAIGNS_PAGINATION_DEFAULT_FILTERS,
  UseAdCampaignsPaginationPresenterProps,
  UseAdCampaignsPaginationPresenter,
} from './types';

const useAdCampaignsPaginationPresenter = ({
  getAdCampaigns,
  getMoreAdCampaigns,
  readAdCampaigns,
  mediaType,
}: UseAdCampaignsPaginationPresenterProps): UseAdCampaignsPaginationPresenter => {
  const companyIds = useAccountCompanyIds();

  const filtersRef = useRef<AdCampaignQueryFilter>({
    ...AD_CAMPAIGNS_PAGINATION_DEFAULT_FILTERS,
    companyIds,
    mediaType,
  });

  const paginationRef = useRef<AdCampaignQueryPagination>(DEFAULT_SHORT_PAGINATION);

  const sortRef = useRef<AdCampaignQuerySort>(AdCampaignQuerySort.IdDesc);

  const allPointsCount = useRef<null | number>(null);

  const cachedAdCampaigns = readAdCampaigns({
    filters: filtersRef.current,
    pagination: paginationRef.current,
    sort: sortRef.current,
  });

  const resetFilters = () => {
    filtersRef.current = {
      ...AD_CAMPAIGNS_PAGINATION_DEFAULT_FILTERS,
      companyIds,
      mediaType,
    };

    paginationRef.current = DEFAULT_SHORT_PAGINATION;

    sortRef.current = AdCampaignQuerySort.IdDesc;
  };

  const requestAdCampaigns = async (
    filters: AdCampaignQueryFilter = AD_CAMPAIGNS_PAGINATION_DEFAULT_FILTERS,
    pagination: AdCampaignQueryPagination = DEFAULT_SHORT_PAGINATION
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await getAdCampaigns({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const loadMoreAdCampaigns = async () => {
    const page = (cachedAdCampaigns?.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await getMoreAdCampaigns({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const onFilterExecute = async (newFilters: AdCampaignQueryFilter) => {
    const preparedFilters: AdCampaignQueryFilter = {
      companyIds,
    };

    const { query, companyTagIds } = newFilters;

    if (query !== undefined) {
      preparedFilters.query = query;
      filtersRef.current.query = query;
    }

    if (preparedFilters.query === undefined && filtersRef.current.query) {
      preparedFilters.query = filtersRef.current.query;
    }

    if (companyTagIds !== undefined) {
      preparedFilters.companyTagIds = companyTagIds;
      filtersRef.current.companyTagIds = companyTagIds;
    }

    if (preparedFilters.companyTagIds === undefined && filtersRef.current.companyTagIds) {
      preparedFilters.companyTagIds = filtersRef.current.companyTagIds;
    }

    if (preparedFilters.statuses === undefined && filtersRef.current.statuses) {
      preparedFilters.statuses = filtersRef.current.statuses;
    }

    await requestAdCampaigns(preparedFilters);
  };

  useEffect(() => {
    if (
      cachedAdCampaigns?.paginationInfo?.totalCount !== undefined &&
      (allPointsCount.current === null || allPointsCount.current === 0)
    ) {
      allPointsCount.current = cachedAdCampaigns.paginationInfo?.totalCount || 0;
    }
  }, [cachedAdCampaigns?.paginationInfo]);

  return {
    adCampaigns: cachedAdCampaigns?.result || [],
    paginationInfo: cachedAdCampaigns?.paginationInfo || DEFAULT_PAGINATION_INFO,
    allAdCampaignsCount: allPointsCount.current,
    filtersRef,
    requestAdCampaigns,
    loadMoreAdCampaigns,
    resetFilters,
    onFilterExecute,
  };
};

export default useAdCampaignsPaginationPresenter;
